import {
  DateFragment,
  DateTime,
  PartialDate,
  TimeOfDay,
  TimezoneRegion,
  Utc,
} from "@tento-chrono";
import { type SheetRow, SheetViewModel } from "@tento-lona";
import { DataAliasPlugin } from "../../data.ts";
import type { CalendarSubcolumnSpan } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import type { CellEntry, Option } from "@tento-lona/sheets";
import { Span as ContentSpan } from "@tento-ui/components/span/span";
import { Column } from "@tento-ui/component-builtin";
import { SpanFlat } from "@tento-ui/components/span/components/span-flat";
import { SpanColumn } from "@tento-ui/components/span/components/span-column";
import { LonaIcons, MoonFillIcon } from "@tento-ui/ui/icons";
import type { SheetBinder } from "../../../../sheet/sheet-index-renderer.ts";

export namespace SpanSleep {
  export function makeSpan(id: string, time: DateTime.Range<Utc>): CalendarSubcolumnSpan {
    const $icon = LonaIcons.make(MoonFillIcon, {
      sizePx: 12,
      color: "var(--bg)",
    });

    const $container = Column.make();
    $container.appendChild($icon);
    $container.assignStyles({
      borderRadius: "50%",
      padding: "2px",
      color: "var(--bg)",
    });

    const $e = SpanFlat.make();
    $e.style.setProperty("--icon-bg", "transparent");
    $e.style.background = "var(--purple)";
    $e.bind($container);

    return {
      id,
      bindContent: ContentSpan.staticContent($e),
      start: time.start,
      end: time.end,
      pointerEvents: "all",
      item: null,
      callbacks: null,
    };
  }

  type SleepCellData = {
    id: string;
    start: string;
    end: string;
  };

  function sleepCellData(cell: CellEntry): Option<SleepCellData> {
    const data = cell.value;
    if (!data || typeof data !== "object") return null;
    if (Array.isArray(data)) return null;
    if (!("start" in data) || typeof data.start !== "string") return null;
    if (!("end" in data) || typeof data.end !== "string") return null;
    const id = "id" in data && typeof data.id === "string"
      ? data.id
      : cell.date.toString();
    return { id, start: data.start, end: data.end };
  }

  function makeSpanFromCell(cell: CellEntry): Option<CalendarSubcolumnSpan> {
    const sleepData = sleepCellData(cell);
    if (!sleepData) return null;

    const start = DateTime.fromRfc3339(sleepData.start).exp().toUtc();
    const end = DateTime.fromRfc3339(sleepData.end).exp().toUtc();

    return {
      ...makeSpan(
        sleepData.id,
        new DateTime.Range(start, end),
      ),
      pointerEvents: "none",
    };
  }

  export const makeSpansFactory =
    (
      plugin: DataAliasPlugin,
      sheet: SheetViewModel.Surface<SheetBinder>,
      row: SheetRow.Joined.Handle,
    ) =>
    async (
      windowed: DateFragment.Windowed,
      tz: TimezoneRegion,
    ): Promise<CalendarSubcolumnSpan[]> => {
      const pd = PartialDate.fromDate(windowed.fragment.date);
      const pdtz: [PartialDate, TimezoneRegion] = [pd, tz];

      const dataFrame = await plugin.context.store.getAllInRange(
        sheet,
        row,
        pdtz,
        [],
      );

      const $cells = dataFrame.cells
        .map(makeSpanFromCell)
        .filter((s): s is CalendarSubcolumnSpan => s != null);
      return $cells;
    };

  export function makeLayout(): HTMLElement {
    const $c = SpanColumn.make();
    $c.bind([24]);
    return $c;
  }
}
